import type { ComputedRef, Ref } from 'vue';

import { computed, ref } from 'vue';

import type { Inverters } from "~/types/equipment"

type InvertersDataGetter = () => Inverters.Inverter[] | undefined;
type SortableColumn = keyof Inverters.Inverter;

type SortDirection = '' | 'asc' | 'desc';

type SortState = {
  column: string;
  direction: SortDirection;
};

type UseTableSortingReturn = {
  handleSort: (_column: string) => void;
  sortedInverters: ComputedRef<Inverters.Inverter[]>;
  sortState: Ref<SortState>;
};

const NUMERIC_COLUMNS: readonly SortableColumn[] = [
  'count_key',
  'id',
  'overload',
  'phase_id',
  'power',
  'tension',
] as const;

const DATE_COLUMNS: readonly SortableColumn[] = [
  'createdAt',
  'update_date',
] as const;

const BOOLEAN_COLUMNS: readonly SortableColumn[] = [
  'is_hybrid',
] as const;

const SORT_DIRECTIONS = {
  ASC: 'asc' as const,
  DESC: 'desc' as const,
  NONE: '' as const,
} as const;

const DEFAULT_NUMERIC_VALUE = 0;
const DEFAULT_DATE_VALUE = 0;
const HYBRID_TRUE_VALUE = 1;
const HYBRID_FALSE_VALUE = 0;
const DEFAULT_STRING_VALUE = '';

const isNumericColumn = (column: string): column is SortableColumn => (
  NUMERIC_COLUMNS.includes(column as SortableColumn)
);

const isDateColumn = (column: string): column is SortableColumn => (
  DATE_COLUMNS.includes(column as SortableColumn)
);

const isBooleanColumn = (column: string): column is SortableColumn => (
  BOOLEAN_COLUMNS.includes(column as SortableColumn)
);

/**
 * Hook to manage table sorting for inverters
 *
 * @param inverters - Function that returns the array of inverters to sort
 * @returns Object with sort state, sorted inverters and sort function
 *
 * @example
 * ```typescript
 * const { sortState, sortedInverters, handleSort } = useTableSorting(() => inverters.value?.items);
 * ```
 */
export default function useTableSorting(inverters: InvertersDataGetter): UseTableSortingReturn {
  const sortState = ref<SortState>({
    column: SORT_DIRECTIONS.NONE,
    direction: SORT_DIRECTIONS.NONE,
  });

  const getColumnValue = (item: Inverters.Inverter, column: string): number | string => {
    if (isNumericColumn(column)) {
      return (item[column] as number) ?? DEFAULT_NUMERIC_VALUE;
    }

    if (isBooleanColumn(column)) {
      return item.is_hybrid ? HYBRID_TRUE_VALUE : HYBRID_FALSE_VALUE;
    }

    if (isDateColumn(column)) {
      const dateValue = item[column] as string | undefined;
      return dateValue ? new Date(dateValue).getTime() : DEFAULT_DATE_VALUE;
    }

    const stringValue = item[column as SortableColumn];
    return stringValue?.toString().toLowerCase() ?? DEFAULT_STRING_VALUE;
  };

  const compareValues = (
    firstValue: number | string,
    secondValue: number | string,
    isAscending: boolean,
  ): number => {
    if (typeof firstValue === 'number' && typeof secondValue === 'number') {
      return isAscending ? firstValue - secondValue : secondValue - firstValue;
    }

    const firstStr = String(firstValue);
    const secondStr = String(secondValue);

    return isAscending
      ? firstStr.localeCompare(secondStr)
      : secondStr.localeCompare(firstStr);
  };

  const sortedInverters = computed<Inverters.Inverter[]>(() => {
    const data = inverters();
    if (!data || data.length === 0) {
      return [];
    }

    const { column, direction } = sortState.value;
    if (!column || direction === SORT_DIRECTIONS.NONE) {
      return data;
    }

    const isAscending = direction === SORT_DIRECTIONS.ASC;

    return [...data].sort((firstItem, secondItem) => {
      const firstValue = getColumnValue(firstItem, column);
      const secondValue = getColumnValue(secondItem, column);
      return compareValues(firstValue, secondValue, isAscending);
    });
  });

  const handleSort = (column: string): void => {
    const currentColumn = sortState.value.column;
    const currentDirection = sortState.value.direction;

    if (currentColumn === column) {
      sortState.value.direction = currentDirection === SORT_DIRECTIONS.ASC
        ? SORT_DIRECTIONS.DESC
        : SORT_DIRECTIONS.ASC;
    } else {
      sortState.value.column = column;
      sortState.value.direction = SORT_DIRECTIONS.ASC;
    }
  };

  return {
    handleSort,
    sortedInverters,
    sortState,
  };
}
